/**
 * The grown-up screens in the family's home language (Sam, 25 Oct 2026).
 *
 * The English text is the key and the fallback: `t('Settings')` gives the
 * translation when there is one and the English otherwise, so a line not yet
 * translated still reads, in English. Only grown-up screens call `t()`; the
 * children's screens stay English, because English is what they are learning.
 *
 * Adding a language: write its file like i18n-zh-TW.ts, add it to SCREEN
 * below, and add its code to SCREEN_LANGUAGES in api/routes_parent.py. Every
 * translation should be read by a native speaker before it goes live.
 */
import { createElement, Fragment, type ReactNode } from 'react'
import { ES } from './i18n-es'
import { ZH_TW } from './i18n-zh-TW'

export type ScreenLanguage = 'en' | 'zh-TW' | 'es'

const SCREEN: Record<Exclude<ScreenLanguage, 'en'>, Record<string, string>> = {
  'zh-TW': ZH_TW,
  es: ES,
}

/** Languages the grown-up screens can be shown in, besides English. */
export const SCREEN_LANGUAGES: { code: ScreenLanguage; label: string }[] = [
  { code: 'zh-TW', label: '中文（繁體）' },
  { code: 'es', label: 'Español' },
]

/** The answers to "Do you speak a language other than English at home?" */
export const HOME_LANGUAGES: { code: string | null; label: string }[] = [
  { code: null, label: 'English only' },
  { code: 'zh-TW', label: '中文（繁體）Chinese, Traditional' },
  { code: 'zh-CN', label: '中文（简体）Chinese, Simplified' },
  { code: 'es', label: 'Español · Spanish' },
  { code: 'ko', label: '한국어 · Korean' },
  { code: 'ja', label: '日本語 · Japanese' },
  { code: 'vi', label: 'Tiếng Việt · Vietnamese' },
  { code: 'tl', label: 'Tagalog' },
  { code: 'fr', label: 'Français · French' },
  { code: 'ar', label: 'العربية · Arabic' },
  { code: 'other', label: 'Another language' },
]

/** The screen language that suits a home language, if it is written yet. */
export function screenLanguageFor(home: string | null | undefined): ScreenLanguage | null {
  if (home === 'zh-TW' || home === 'zh-CN') return 'zh-TW'
  if (home === 'es') return 'es'
  return null
}

let current: ScreenLanguage = 'en'
let home: string | null = null

/** Set from the signed-in grown-up on every launch (App.tsx), and on a change. */
export function setScreenLanguage(lang: string | null | undefined, homeLanguage?: string | null): void {
  current = lang === 'zh-TW' || lang === 'es' ? lang : 'en'
  if (homeLanguage !== undefined) home = homeLanguage
}

export function screenLanguage(): ScreenLanguage {
  return current
}

/** The answer to the set-up question, or null for English only. */
export function homeLanguage(): string | null {
  return home
}

/**
 * The grown-up screen text in the chosen language, or the English.
 *
 * `values` fill `{name}` slots, so a sentence with a number inside is one
 * text and each language puts the number where its word order wants it.
 */
export function t(en: string, values?: Record<string, string | number>): string {
  const text = current === 'en' ? en : SCREEN[current][en] ?? en
  if (!values) return text
  return fill(text, values)
}

/**
 * The same as `t`, but always English. For a piece shared by both kinds of
 * screen (the loading screen, the PIN keypad, the buddy maker): it takes
 * `grownUp` and picks `t` or this, so the boys' copy stays English.
 */
export function english(en: string, values?: Record<string, string | number>): string {
  return values ? fill(en, values) : en
}

/** `t` for a sentence with a bold name or number inside (28 Oct 2026). */
export function tn(en: string, values: Record<string, ReactNode>): ReactNode[] {
  const text = current === 'en' ? en : SCREEN[current][en] ?? en
  return text.split(/(\{\w+\})/).map((part, i) => {
    const key = /^\{(\w+)\}$/.exec(part)?.[1]
    return createElement(Fragment, { key: i }, key && key in values ? values[key] : part)
  })
}

/**
 * A server message on a grown-up screen, translated. A message with a word or
 * a name inside comes as the sentence with a gap plus the word (api/said.py
 * on the server), so the translation is looked up by the gapped sentence and
 * the word put back in (round 14, 28 Oct 2026).
 */
export function fromServer(err: unknown, fallback: string): string {
  const e = err as { detail?: string; template?: string; values?: Record<string, string> } | null
  if (e?.template) return t(e.template, e.values)
  return t(e?.detail || fallback)
}

/**
 * The language picked on the front page, kept in this browser. Before sign-in
 * the app does not know whose account it is, so the sign-in, agreement and
 * trial screens would otherwise always be English (round 14). The account's
 * own choice wins once it has one.
 */
const REMEMBER = 'ws-screen-language'

export function rememberedScreenLanguage(): ScreenLanguage {
  try {
    const kept = window.localStorage.getItem(REMEMBER)
    return kept === 'zh-TW' || kept === 'es' ? kept : 'en'
  } catch {
    return 'en'
  }
}

export function rememberScreenLanguage(lang: ScreenLanguage): void {
  try { window.localStorage.setItem(REMEMBER, lang) } catch { /* private window: this visit only */ }
}

/** Dates on grown-up screens in the chosen language ("28 Oct" / "10月28日"). */
export function dateLocale(): string | undefined {
  return current === 'en' ? undefined : current
}

function fill(text: string, values: Record<string, string | number>): string {
  return text.replace(/\{(\w+)\}/g, (slot, key: string) => (key in values ? String(values[key]) : slot))
}
